import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { requestIdleCallback } from '@theme/utilities';

/**
 * Bundle contents on the PDP.
 *
 * Liquid cannot tell whether a variant is a native bundle — only cart lines
 * expose `item_components` — so the component list is fetched from the
 * Storefront API and the accordion is painted only once the response confirms
 * it. Everything fails to "render nothing": network error, GraphQL error,
 * `requiresComponents: false` or an empty component list all leave the host
 * empty rather than showing a heading with no list.
 *
 * The request is tokenless. A Storefront access token only sees products
 * published to its own sales channel, whereas tokenless reads the Online Store
 * publication — the same catalogue this theme renders. Tokenless cannot read
 * metafields, which is why linkability rests on `onlineStoreUrl` alone.
 *
 * The API version lives in `snippets/bundle-contents.liquid` (`data-api-url`),
 * which is the single place it is declared.
 *
 * See `docs/business-rules/bundle-pdp-contents.md`.
 */

const COUNT_PLACEHOLDER = '__COUNT__';
const QUANTITY_PLACEHOLDER = '__QTY__';
const TITLE_PLACEHOLDER = '__TITLE__';

/**
 * Liquid emits locale codes as `en` / `pt-BR`; the `CountryCode` and
 * `LanguageCode` enums want `EN` / `PT_BR`, and reject anything else outright.
 *
 * @param {string | undefined} value
 * @returns {string | null}
 */
function toLocaleEnum(value) {
  const trimmed = value?.trim();

  return trimmed ? trimmed.toUpperCase().replace(/-/g, '_') : null;
}

/**
 * Shopify's documented ceiling: fixed bundles allow 30 components, dynamic
 * bundles 150. Requesting the larger limit means the list can never be
 * silently truncated, which would also make the "What's included (N)" count
 * lie. Raise this if Shopify raises the ceiling.
 */
const MAX_COMPONENTS = 150;

const QUERY = `
  query BundleComponents($id: ID!, $country: CountryCode, $language: LanguageCode)
  @inContext(country: $country, language: $language) {
    node(id: $id) {
      ... on ProductVariant {
        requiresComponents
        components(first: ${MAX_COMPONENTS}) {
          nodes {
            quantity
            productVariant {
              id
              image { url(transform: { maxWidth: 240, maxHeight: 240 }) }
              product {
                title
                vendor
                onlineStoreUrl
                featuredImage { url(transform: { maxWidth: 240, maxHeight: 240 }) }
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * @typedef {object} BundleComponentNode
 * @property {number} quantity
 * @property {{
 *   id: string,
 *   image: { url: string } | null,
 *   product: {
 *     title: string,
 *     vendor: string | null,
 *     onlineStoreUrl: string | null,
 *     featuredImage: { url: string } | null,
 *   } | null,
 * } | null} productVariant
 */

/**
 * @typedef {object} BundleContentsRefs
 * @property {HTMLTemplateElement} shellTemplate
 * @property {HTMLTemplateElement} tileTemplate
 */

/** @extends {Component<BundleContentsRefs>} */
class BundleContentsComponent extends Component {
  requiredRefs = ['shellTemplate', 'tileTemplate'];

  /** @type {AbortController} */
  #controller = new AbortController();

  /** Component lists already fetched this page load, keyed by variant GID. */
  #cache = new Map();

  /** Guards against an earlier in-flight request painting over a later one. */
  #requestId = 0;

  connectedCallback() {
    super.connectedCallback();

    // Fresh controller per connection: the section rendering API can detach and
    // reattach this element, and the previous one is aborted on disconnect.
    this.#controller = new AbortController();

    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, {
      signal: this.#controller.signal,
    });

    // Almost no PDP is a bundle, so this request must not compete with the
    // initial render of the pages that will never use it.
    requestIdleCallback(() => this.#refresh());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * @param {Event} event - `variant:update`, listened for on `document` because
   *   this section is a sibling of the product information section, so the
   *   usual `closest('[id^="ProductInformation-"]')` lookup finds nothing.
   * @returns {void}
   */
  #handleVariantUpdate = (event) => {
    const detail = /** @type {{ resource?: { id?: string | number } | null, data?: { productId?: string, newProduct?: { id?: string } } }} */ (
      /** @type {CustomEvent} */ (event).detail
    );

    const newProductId = detail?.data?.newProduct?.id;

    // A combined listing swaps the product itself, so the event is ours even
    // though the product id no longer matches the one we were rendered with.
    if (!newProductId && String(detail?.data?.productId ?? '') !== this.dataset.productId) return;

    const variantId = detail?.resource?.id;
    if (variantId == null) {
      this.#clear();
      return;
    }

    if (newProductId) this.dataset.productId = String(newProductId);
    this.dataset.variantId = String(variantId);

    this.#refresh();
  };

  /**
   * Resolves the selected variant's components and repaints.
   * @returns {Promise<void>}
   */
  async #refresh() {
    const gid = this.#variantGid();
    if (!gid) return;

    const requestId = ++this.#requestId;

    const cached = this.#cache.get(gid);

    if (cached) {
      this.#paint(cached);
      return;
    }

    // The previous variant's components must not sit under a different
    // selection while this resolves.
    if (this.isConnected) this.#clear();

    const components = await this.#fetchComponents(gid);

    if (!components) {
      // A failed lookup leaves nothing rather than the wrong bundle.
      return;
    }

    this.#cache.set(gid, components);

    if (requestId !== this.#requestId) return;

    this.#paint(components);
  }

  /**
   * @returns {string | null} The selected variant as a Storefront global id.
   */
  #variantGid() {
    const id = (this.dataset.variantId ?? '').trim();
    if (!/^\d+$/.test(id)) return null;

    return `gid://shopify/ProductVariant/${id}`;
  }

  /**
   * @param {string} gid - Variant global id.
   * @returns {Promise<BundleComponentNode[] | null>} Components, an empty array
   *   for a confirmed non-bundle, or null when the answer is unknown.
   */
  async #fetchComponents(gid) {
    const { apiUrl, country, language } = this.dataset;
    if (!apiUrl) return null;

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: QUERY,
          variables: {
            id: gid,
            country: toLocaleEnum(country),
            language: toLocaleEnum(language),
          },
        }),
        signal: this.#controller.signal,
      });

      if (!response.ok) return null;

      const payload = await response.json();
      if (payload.errors) return null;

      const node = payload.data?.node;
      if (!node?.requiresComponents) return [];

      return node.components?.nodes ?? [];
    } catch {
      // Aborted, offline, or malformed: stay empty rather than half-render.
      return null;
    }
  }

  /**
   * @param {BundleComponentNode[]} components
   * @returns {void}
   */
  #paint(components) {
    // A response can land after a section morph detached this element. Aborting
    // on disconnect cannot retract a promise that already resolved, and
    // painting here would set the document-level flag for a page we no longer
    // belong to, stranding the BUNDLE chip.
    if (!this.isConnected) return;

    this.#clear();
    if (!components.length) return;

    const shell = this.refs.shellTemplate.content.cloneNode(true);
    if (!(shell instanceof DocumentFragment)) return;

    const grid = shell.querySelector('.bundle-contents__grid');
    const heading = shell.querySelector('.bunnings-heading-with-accent__heading');
    if (!grid || !heading) return;

    this.#setCount(heading, components.length);

    for (const component of components) {
      const tile = this.#buildTile(component);
      if (tile) grid.append(tile);
    }

    if (!grid.childElementCount) return;

    this.append(shell);

    this.#markBadge(true);
  }

  /**
   * Reveals or hides the BUNDLE chip in the product header, which lives in
   * another section. Matched on product id rather than a document-wide flag,
   * because a page can hold a second product-details block (a Featured Product
   * section) whose product is not a bundle.
   *
   * @param {boolean} isBundle
   * @returns {void}
   */
  #markBadge(isBundle) {
    const productId = this.dataset.productId;
    if (!productId) return;

    for (const badge of document.querySelectorAll('.bundle-badge')) {
      if (!(badge instanceof HTMLElement)) continue;
      if (badge.dataset.productId !== productId) continue;

      if (isBundle) badge.dataset.bundle = 'true';
      else delete badge.dataset.bundle;
    }
  }

  /**
   * Substitutes the count into the Liquid-translated heading. Only the text
   * node is touched so the accent bar element survives.
   *
   * @param {Element} heading
   * @param {number} count
   * @returns {void}
   */
  #setCount(heading, count) {
    for (const node of heading.childNodes) {
      if (node.nodeType !== Node.TEXT_NODE) continue;
      if (!node.textContent?.includes(COUNT_PLACEHOLDER)) continue;

      node.textContent = node.textContent.replace(COUNT_PLACEHOLDER, String(count));
      return;
    }
  }

  /**
   * @param {BundleComponentNode} component
   * @returns {Element | null}
   */
  #buildTile(component) {
    const product = component.productVariant?.product;
    if (!product) return null;

    const fragment = this.refs.tileTemplate.content.cloneNode(true);
    if (!(fragment instanceof DocumentFragment)) return null;

    const item = fragment.firstElementChild;
    const tile = fragment.querySelector('.bundle-contents__tile');
    const image = fragment.querySelector('.bundle-contents__image');
    const vendor = fragment.querySelector('.bundle-contents__vendor');
    const title = fragment.querySelector('.bundle-contents__title');
    const quantity = fragment.querySelector('.bundle-contents__quantity');
    const a11yQuantity = fragment.querySelector('.bundle-contents__a11y-quantity');

    if (!item || !(tile instanceof HTMLElement) || !(image instanceof HTMLImageElement)) return null;
    if (!vendor || !title || !quantity || !a11yQuantity) return null;

    image.src =
      component.productVariant?.image?.url ?? product.featuredImage?.url ?? this.dataset.placeholderUrl ?? '';

    vendor.textContent = product.vendor ?? '';
    title.textContent = product.title;

    if (component.quantity > 1) {
      quantity.textContent = String(component.quantity);
      quantity.removeAttribute('hidden');

      // The badge is a glyph, so the readable "2 x Title" replaces the visible
      // title for assistive tech rather than being read alongside it. The
      // phrasing comes from Liquid so it is translated, not assembled here.
      a11yQuantity.textContent = (this.dataset.quantityLabel ?? '')
        .replace(QUANTITY_PLACEHOLDER, String(component.quantity))
        .replace(TITLE_PLACEHOLDER, product.title);
      a11yQuantity.removeAttribute('hidden');
      title.setAttribute('aria-hidden', 'true');
    }

    const href = this.#linkFor(product);
    if (href) {
      const link = document.createElement('a');
      link.className = tile.className;
      link.href = href;
      link.append(...tile.childNodes);
      tile.replaceWith(link);
    }

    return item;
  }

  /**
   * `onlineStoreUrl` is Shopify's own answer to "is this published and
   * reachable", so it covers draft and unpublished components without a
   * metafield read. Unlisted components are not filtered — no other surface in
   * this theme filters on `custom.shopify_status` either.
   *
   * @param {{ onlineStoreUrl: string | null }} product
   * @returns {string | null} An absolute storefront URL, or null when the
   *   component must render as plain text.
   */
  #linkFor(product) {
    // A raw PDP link would drop the customer out of the kiosk shell.
    const root = document.documentElement.classList;
    if (root.contains('kiosk-mode') && !root.contains('kiosk-mode--browsable')) return null;

    const url = product.onlineStoreUrl;
    if (!url) return null;

    try {
      return new URL(url).protocol === 'https:' ? url : null;
    } catch {
      return null;
    }
  }

  /** @returns {void} */
  #clear() {
    this.#markBadge(false);

    for (const child of [...this.children]) {
      if (child instanceof HTMLTemplateElement) continue;
      child.remove();
    }
  }
}

if (!customElements.get('bundle-contents-component')) {
  customElements.define('bundle-contents-component', BundleContentsComponent);
}
